import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Slider } from '@mui/material';
import Cookies from 'js-cookie';

import { setPrequalificationPortal } from 'actions/prequalificationPortal';
import { setData } from 'actions/data';
import { ContractorNameSource } from 'enums/ApplicationsVariables';
import { setApplication } from 'actions/application';
import Loader from 'components/Loader';
import FormHeader from 'components/FormHeader';
import FormBody from 'components/FormBody';
import NumberInput from 'components/Inputs/NumberInput';
import { MAX_LOAN_AMOUNT_LENGTH } from 'utils/constants';
import { Button } from 'components/Buttons/Button';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { LoadingType, PreQualificationStep, setLoadingState, setPreQualificationStep } from 'actions';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { getBorrowerByToken } from 'thunks';
import {
  getErrorMessageForRequiredFields,
  MONETARY_PREFIX,
  removeCurrencySign,
  removeNonDigits,
} from 'utils/stringUtils';
import { CheckedImage } from 'assets/icons';
import { LOAN_OPTIONS_NAMES } from 'pages/Dashboard/steps/AcceptOffer/acceptOffer.constants';
import { setBorrower } from 'actions/borrower';
import { intermediariesApi } from 'api/IntermediariesApi';

import { LOAN_SLIDER_MARKS, MAX_LOAN_FOR_PORTAL, MAXIMUM_STEPS, MIN_LOAN_AMOUNT } from './loanAmount.constants';
import {
  denormalizeLoanAmountSliderValue,
  normalizeValue,
  roundToStep,
  checkUserShouldBeRedirectToFinMktPortal,
} from './loanAmount.utils';
import styles from './LoanAmount.module.scss';
import LoanAgreementPopup from './LoanAgreementPopup';

const LoanInformation = () => {
  const dispatch = useAppDispatch();

  const application = useAppSelector((state) => state.application);
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [isLoanAgreementPopupOpen, setIsLoanAgreementPopupOpen] = useState(false);

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search.slice(1));

  const {
    register,
    unregister,
    formState: { errors },
    setValue,
    trigger,
    watch,
    clearErrors,
    handleSubmit,
  } = useForm({
    defaultValues: {
      [LoanInformationVariables.LoanAmount]: +(application[LoanInformationVariables.LoanAmount] || 0),
    },
  });

  const watcher = watch();
  const [sliderValue, setSliderValue] = useState(normalizeValue(watcher[LoanInformationVariables.LoanAmount]));

  const getCurrentBorrower = async () => {
    try {
      await dispatch(getBorrowerByToken()).unwrap();

      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: true }));
    } catch (error) {
      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: false }));

      localStorage.clear();
    }
  };

  const handleContractorInfo = async (intermediaryId: string): Promise<void> => {
    const isVerified = await intermediariesApi.verify(intermediaryId);
    const contractorName = params.getAll('contractor_name')[0];

    if (isVerified && !!contractorName) {
      dispatch(setPrequalificationPortal({ isContractorNameInputDisabled: true }));

      dispatch(
        setData({
          contractorName: contractorName,
          contractorAutofillUsed: ContractorNameSource.PromoLink,
          contractorPromoLinkUsed: true,
          promoCode: params.getAll('promo_code')[0],
        }),
      );
    } else {
      dispatch(setPrequalificationPortal({ isContractorNameInputDisabled: false }));

      dispatch(
        setData({
          contractorAutofillUsed: ContractorNameSource.Empty,
          contractorPromoLinkUsed: false,
        }),
      );
    }
  };

  const saveApplicationData = async () => {
    try {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      const intermediaryId = params.getAll('intermediary_id')[0];
      const isLoggedIn = !!localStorage.getItem('token');

      if (intermediaryId) {
        await handleContractorInfo(intermediaryId);
      }

      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: false }));

      if (isLoggedIn) {
        await getCurrentBorrower();
      }
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  useEffect(() => {
    saveApplicationData();
  }, []);

  useEffect(() => {
    register(LoanInformationVariables.LoanAmount, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanAmount),
      validate: (value) => {
        const loanAmount = +removeNonDigits(value);

        if (loanAmount > MAX_LOAN_FOR_PORTAL) {
          return 'Loan Amount must be less or equal to $500,000';
        }

        if (loanAmount < MIN_LOAN_AMOUNT) {
          return 'Loan Amount must be greater or equal to $1,000';
        }
      },
    });
  }, [register, unregister, watcher]);

  const parseQueries = () => {
    dispatch(
      setData({
        device: params.getAll('device')[0],
        network: params.getAll('network')[0],
        refer: params.getAll('refer')[0],
        isIframe: !!params.getAll('isIframe')[0],
        campaignId: params.getAll('campaignid')[0],
        adGroupId: params.getAll('adgroupid')[0],
        utmSource: Cookies.get('pys_utm_source'),
        utmMedium: Cookies.get('pys_utm_medium'),
        utmCampaign: Cookies.get('pys_utm_campaign'),
        utmTerm: Cookies.get('pys_utm_term'),
        utmContent: Cookies.get('pys_utm_content'),
        gclid: Cookies.get('_gcl_aw'),
        fbclid: Cookies.get('_fbc'),
        liFatId: Cookies.get('li_fat_id'),
        muId: Cookies.get('MUID'),
        uetMsclkId: Cookies.get('_uetmsclkid'),
      }),
    );
  };

  const validateLoanForm = handleSubmit(async () => {
    const loanAmount = +removeNonDigits(watcher[LoanInformationVariables.LoanAmount]);

    if (checkUserShouldBeRedirectToFinMktPortal(loanAmount)) {
      window.location.href = process.env.REACT_APP_LOW_LOAN_AMOUNT_REDIRECT_URL!;

      return;
    }

    setIsLoanAgreementPopupOpen(true);
  });

  const handleLoanAmountConfirmation = () => {
    parseQueries();

    dispatch(
      setApplication({
        [LoanInformationVariables.LoanAmount]: +removeNonDigits(watcher[LoanInformationVariables.LoanAmount]),
      }),
    );

    dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
  };

  const handleLoanAmountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +removeNonDigits(removeCurrencySign(event.target.value));

    clearErrors(LoanInformationVariables.LoanAmount);
    setValue(LoanInformationVariables.LoanAmount, newValue);

    setSliderValue(normalizeValue(newValue));

    trigger();
  };

  const handleChange = (event: any, newValue: number | number[]) => {
    const denormalizedValue = denormalizeLoanAmountSliderValue(newValue as number);
    const roundedValue = roundToStep(denormalizedValue);

    setSliderValue(normalizeValue(roundedValue));
    setValue(LoanInformationVariables.LoanAmount, roundedValue);
  };

  const handleCloseLoanAgreementPopup = () => {
    setIsLoanAgreementPopupOpen(false);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.infoContent}>Inquiry Does NOT Affect Your Credit Scores</div>
          </div>
          <FormHeader title="Loan Information" percent={0} />
          <div className={styles.rowContainer}>
            <FormBody formBodyClassName={styles.formBody}>
              <NumberInput
                label={'How much do you want to borrow?'}
                prefix={MONETARY_PREFIX}
                placeholder={MONETARY_PREFIX}
                thousandSeparator
                inputContainerClassName={styles.loanAmountInputContainer}
                inputClassName={styles.loanAmountInput}
                labelClassName={styles.loanAmountLabel}
                name={LoanInformationVariables.LoanAmount}
                onChange={handleLoanAmountInputChange}
                errorMessage={errors[LoanInformationVariables.LoanAmount]?.message}
                value={watcher[LoanInformationVariables.LoanAmount]}
                maxLength={MAX_LOAN_AMOUNT_LENGTH}
                allowNegative={false}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
              <Slider
                aria-label="Loan Amount"
                value={sliderValue}
                step={1}
                marks={LOAN_SLIDER_MARKS}
                min={0}
                max={MAXIMUM_STEPS}
                classes={{
                  root: styles.loanAmountSlider,
                  mark: styles.loanAmountSliderMark,
                  markLabel: styles.loanAmountSliderMarkLabel,
                  rail: styles.loanAmountSliderRail,
                  thumb: styles.loanAmountSliderThumb,
                }}
                valueLabelDisplay="auto"
                onChange={handleChange}
                valueLabelFormat={(value) =>
                  `$${Math.round(denormalizeLoanAmountSliderValue(value)).toLocaleString('en-US')}`
                }
              />
            </FormBody>
            <div className={styles.options}>
              {LOAN_OPTIONS_NAMES.map((option) => (
                <div key={option} className={styles.option}>
                  <CheckedImage />
                  <p>{option}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.nextButton}>
            <Button onClick={validateLoanForm} withArrow>
              Next
            </Button>
          </div>
          <LoanAgreementPopup
            isModalOpen={isLoanAgreementPopupOpen}
            onSubmit={handleLoanAmountConfirmation}
            onClose={handleCloseLoanAgreementPopup}
          />
        </>
      )}
    </>
  );
};

export default LoanInformation;
